import { useEffect, useMemo, useState } from "react";
import { Dialog } from "@/components/ui/Dialog";
import { Button } from "@/components/ui/Button";
import { Alert } from "@/components/ui/Alert";
import {
  compareAssignmentLinks,
  getAssignmentLinks,
  unlinkAssignment,
  updateAssignmentLinks,
  updateAssignmentSeriesMode,
  type PlanungAssignment,
  type PlanungAssignmentLinkCompare,
  type PlanungAssignmentLinkPeer,
  type PlanungBoard,
} from "@/lib/montageplanung";
import { formatDateDe } from "./dateUtils";
import { rowLabelFull } from "./displayLabels";
import { AssignmentLinkResolveDialog } from "./AssignmentLinkResolveDialog";
import { useConfirmDialog } from "./useConfirmDialog";

function formatRange(from: string, to: string): string {
  const f = from.slice(0, 10);
  const t = to.slice(0, 10);
  return f === t ? formatDateDe(f) : `${formatDateDe(f)} – ${formatDateDe(t)}`;
}

export interface AssignmentLinksDialogProps {
  open: boolean;
  onClose: () => void;
  assignment: PlanungAssignment | null;
  board: PlanungBoard | null;
  onChanged: () => void;
  pickReviewMode: boolean;
  pickSelectedIds: number[];
  onStartPickMode: () => void;
  onClearLinkPickState: () => void;
  onClearPickSelection: () => void;
}

export function AssignmentLinksDialog({
  open,
  onClose,
  assignment,
  board,
  onChanged,
  pickReviewMode,
  pickSelectedIds,
  onStartPickMode,
  onClearLinkPickState,
  onClearPickSelection,
}: AssignmentLinksDialogProps) {
  const [links, setLinks] = useState<PlanungAssignmentLinkPeer[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolveOpen, setResolveOpen] = useState(false);
  const [resolveCompare, setResolveCompare] = useState<PlanungAssignmentLinkCompare | null>(null);
  const [resolvePeerIds, setResolvePeerIds] = useState<number[]>([]);
  const [resolveError, setResolveError] = useState<string | null>(null);
  const [seriesMode, setSeriesMode] = useState(false);
  const [seriesNotice, setSeriesNotice] = useState<string | null>(null);
  const { confirm, dialog: confirmDialog } = useConfirmDialog();

  const rowById = useMemo(() => {
    const map = new Map<number, string>();
    for (const row of board?.rows ?? []) {
      map.set(row.id, rowLabelFull(row));
    }
    return map;
  }, [board?.rows]);

  const liveSeries = useMemo(() => {
    if (!assignment) return false;
    const live = board?.assignments.find((item) => item.id === assignment.id);
    return !!(live?.is_series ?? assignment.is_series);
  }, [assignment, board?.assignments]);

  useEffect(() => {
    setSeriesMode(liveSeries);
  }, [assignment?.id, liveSeries]);

  useEffect(() => {
    if (!open || !assignment) {
      setLinks([]);
      setError(null);
      setSeriesNotice(null);
      setResolveOpen(false);
      setResolveCompare(null);
      setResolvePeerIds([]);
      setResolveError(null);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    setSeriesNotice(null);
    getAssignmentLinks(assignment.id)
      .then((items) => {
        if (!cancelled) setLinks(items);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Verknüpfungen konnten nicht geladen werden.");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, assignment?.id]);

  const pickSelectedAssignments = useMemo(() => {
    if (!board) return [];
    return board.assignments.filter((a) => pickSelectedIds.includes(a.id));
  }, [board, pickSelectedIds]);

  async function refreshLinks() {
    if (!assignment) return;
    const items = await getAssignmentLinks(assignment.id);
    setLinks(items);
  }

  async function finalizeLink(peerIds: number[], contentSourceId?: number) {
    if (!assignment || peerIds.length === 0) return;
    setActionLoading(true);
    setError(null);
    setResolveError(null);
    try {
      await updateAssignmentLinks(assignment.id, peerIds, contentSourceId);
      onClearPickSelection();
      onClearLinkPickState();
      setResolveOpen(false);
      setResolveCompare(null);
      setResolvePeerIds([]);
      await refreshLinks();
      onChanged();
    } catch (err) {
      const message =
        err instanceof Error ? err.message : "Verknüpfung konnte nicht angelegt werden.";
      if (resolveOpen) setResolveError(message);
      else setError(message);
    } finally {
      setActionLoading(false);
    }
  }

  async function handleLinkSelected(peerIds: number[]) {
    if (!assignment || peerIds.length === 0) return;
    setActionLoading(true);
    setError(null);
    try {
      const compare = await compareAssignmentLinks(assignment.id, peerIds);
      if (compare.has_differences) {
        setResolvePeerIds(peerIds);
        setResolveCompare(compare);
        setResolveOpen(true);
      } else {
        await finalizeLink(peerIds, assignment.id);
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Vergleich der Einsätze konnte nicht durchgeführt werden.",
      );
    } finally {
      setActionLoading(false);
    }
  }

  async function handleRemovePeer(peerId: number) {
    if (!assignment) return;
    setActionLoading(true);
    setError(null);
    try {
      await unlinkAssignment(peerId);
      await refreshLinks();
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Verknüpfung konnte nicht gelöst werden.");
    } finally {
      setActionLoading(false);
    }
  }

  async function handleToggleSeries(next: boolean) {
    if (!assignment) return;
    const ok = await confirm({
      title: next ? "Als Serie führen" : "Serie aufheben",
      confirmLabel: next ? "Als Serie führen" : "Serie aufheben",
      variant: "warning",
      message: next
        ? "Die Einsätze bleiben verbunden. Die Feinplanung zeigt zuerst diesen Einsatz, die anderen Termine der Serie können Sie dort öffnen. Projekte gelten nur für den jeweiligen Einsatz und stapeln sich nicht auf vergangenen oder späteren Terminen."
        : "Die Einsätze bleiben verknüpft. Beim nächsten Speichern gelten Projekte und Feinplanung wieder für alle Termine und können dabei überschrieben werden.",
    });
    if (!ok) return;
    setActionLoading(true);
    setError(null);
    setSeriesNotice(null);
    try {
      const result = await updateAssignmentSeriesMode(assignment.id, next);
      setSeriesMode(result.series_mode);
      setSeriesNotice(
        result.series_mode
          ? result.orders_localized
            ? "Serie ist aktiv. Projekte stehen nur noch am Termin, an dem sie in der Feinplanung liegen."
            : "Serie ist aktiv. Neue Projekte gelten nur für den jeweiligen Einsatz. Bereits übernommene Projekte ohne Tagesplanung bleiben vorerst auf jedem Termin, bis Sie sie dort entfernen."
          : "Serie ist aufgehoben. Die Einsätze bleiben verknüpft.",
      );
      onChanged();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Serie konnte nicht geändert werden.");
    } finally {
      setActionLoading(false);
    }
  }

  function handleClose() {
    onClearLinkPickState();
    onClose();
  }

  const canOfferSeries =
    !assignment?.is_company_event && (links.length > 0 || seriesMode);

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        title="Einsatz-Verknüpfungen"
        description={
          assignment
            ? seriesMode
              ? "Serie: die Einsätze bleiben verbunden. Projekte gelten nur für den jeweiligen Termin. Die Feinplanung zeigt zuerst diesen Einsatz."
              : "Verknüpfte Einsätze teilen Inhalt und Feinplanung. Position und Zeitraum bleiben je Einsatz eigenständig."
            : undefined
        }
        size="md"
        zIndexClass="z-[220]"
        footer={
          <Button variant="secondary" onClick={handleClose}>
            Schließen
          </Button>
        }
        bodyClassName="max-h-[min(70vh,520px)] overflow-y-auto"
      >
        {error && (
          <Alert variant="error" className="mb-3">
            {error}
          </Alert>
        )}
        {seriesNotice && (
          <Alert variant="info" className="mb-3">
            {seriesNotice}
          </Alert>
        )}

        {loading ? (
          <p className="text-sm text-label-secondary">Lade Verknüpfungen…</p>
        ) : links.length === 0 ? (
          <p className="text-sm text-label-secondary">Keine verknüpften Einsätze.</p>
        ) : (
          <ul className="space-y-2">
            {links.map((peer) => (
              <li
                key={peer.id}
                className="flex items-start justify-between gap-3 rounded-ios border border-separator bg-fill-tertiary/40 px-3 py-2"
              >
                <div className="min-w-0">
                  <div className="truncate text-sm font-medium text-label">{peer.title}</div>
                  <div className="text-xs text-label-secondary">
                    {rowById.get(peer.row_id) ?? `Zeile #${peer.row_id}`} ·{" "}
                    {formatRange(peer.date_from, peer.date_to)}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className="shrink-0 text-status-red hover:bg-status-red/10"
                  disabled={actionLoading}
                  onClick={() => void handleRemovePeer(peer.id)}
                >
                  Lösen
                </Button>
              </li>
            ))}
          </ul>
        )}

        {canOfferSeries && (
          <div className="mt-4 space-y-2 border-t border-separator pt-4">
            <label className="flex items-start gap-3">
              <input
                type="checkbox"
                className="mt-1"
                checked={seriesMode}
                disabled={actionLoading || loading || links.length === 0}
                onChange={(event) => void handleToggleSeries(event.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-label">Als Serie führen</span>
                <span className="mt-0.5 block text-xs leading-relaxed text-label-secondary">
                  Zum Beispiel ein Termin, der jede Woche kommt. Die Kästen bleiben verbunden,
                  Projekte gelten aber nur für diesen Einsatz. Die Feinplanung zeigt zuerst diesen
                  Einsatz, die anderen Termine können Sie dort öffnen.
                </span>
              </span>
            </label>
          </div>
        )}

        <div className="mt-4 space-y-3 border-t border-separator pt-4">
          <div className="text-sm font-medium text-label">Einsätze verknüpfen</div>

          {pickReviewMode ? (
            <div className="space-y-3 rounded-ios border border-accent/40 bg-accent/5 px-3 py-3">
              {pickSelectedAssignments.length === 0 ? (
                <p className="text-sm text-label-secondary">
                  Noch keine Einsätze ausgewählt. Sie können weitere im Plan auswählen.
                </p>
              ) : (
                <ul className="space-y-1">
                  {pickSelectedAssignments.map((item) => (
                    <li key={item.id} className="text-sm text-label">
                      {item.title} · {rowById.get(item.row_id) ?? `#${item.row_id}`}
                    </li>
                  ))}
                </ul>
              )}
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="primary"
                  size="sm"
                  disabled={actionLoading || pickSelectedIds.length === 0}
                  onClick={() => void handleLinkSelected(pickSelectedIds)}
                >
                  {pickSelectedIds.length > 0
                    ? `${pickSelectedIds.length} Einsatz(e) verknüpfen`
                    : "Verknüpfen"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={actionLoading}
                  onClick={onStartPickMode}
                >
                  Weitere im Plan auswählen…
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={actionLoading || pickSelectedIds.length === 0}
                  onClick={onClearPickSelection}
                >
                  Auswahl leeren
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                variant="primary"
                size="sm"
                disabled={actionLoading}
                onClick={onStartPickMode}
              >
                Einsätze im Plan auswählen…
              </Button>
              <p className="text-xs text-label-secondary">
                Der Dialog schließt sich kurz, damit Sie Einsatz-Balken in der Planung anklicken
                können.
              </p>
            </div>
          )}
        </div>
      </Dialog>

      <AssignmentLinkResolveDialog
        open={resolveOpen}
        onClose={() => {
          if (!actionLoading) {
            setResolveOpen(false);
            setResolveCompare(null);
            setResolvePeerIds([]);
            setResolveError(null);
          }
        }}
        compare={resolveCompare}
        rows={board?.rows ?? []}
        loading={actionLoading}
        error={resolveError}
        onConfirm={(sourceId) => void finalizeLink(resolvePeerIds, sourceId)}
      />
      {confirmDialog}
    </>
  );
}
